import { Navigate, useNavigate } from 'react-router-dom';
import { authService } from '../services/auth.service';
import { useEffect, useState } from 'react';
import { registerNavigationCallback } from '../utils/authUtils';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
  redirectPath?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  allowedRoles = [], 
  redirectPath = '/jobsite-management' 
}) => {
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(true);
  
  // Register navigation callback and check authentication
  useEffect(() => {
    const checkAuthentication = async () => {
      try {
        // Register navigation callback
        registerNavigationCallback(navigate);
        
        // Check if we need to initialize auth state
        if (!authService.isAuthenticated()) {
          const initialized = await authService.initializeAuthState();
          console.log('Auth initialized in protected route:', initialized);
        }
        
        // Check authentication status
        const authenticated = authService.isAuthenticated();
        setIsAuthenticated(authenticated);

        // Check authorization based on user role
        if (authenticated && allowedRoles.length > 0) {
          const user = authService.getUser();
          const hasAccess = allowedRoles.includes(user?.role || '');
          setIsAuthorized(hasAccess);
        }
      } catch (error) {
        console.error('Error checking authentication:', error);
        setIsAuthenticated(false);
      } finally {
        setIsChecking(false);
      }
    };
    
    // Set a timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      setIsChecking(false);
      setIsAuthenticated(authService.isAuthenticated());
    }, 3000); // 3 seconds max loading time
    
    checkAuthentication();
    
    return () => clearTimeout(timeoutId);
  }, [navigate, allowedRoles]);
  
  // Show loading state while checking
  if (isChecking) {
    return <div className="route-loading">Verifying authentication...</div>;
  }
  
  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // If not authorized, redirect to specified path
  if (!isAuthorized) {
    return <Navigate to={redirectPath} replace />;
  }

  return <>{children}</>;
}; 